/**
 * ReasonDialog — AC-hard-3: Shared reason dialog component
 *
 * Extracted from InboxDrawer and InboxPage. Single source of truth for
 * "provide a reason before submitting" dialogs used throughout the Inbox.
 *
 * Behaviour:
 *   - Empty reason is blocked (client-side validation)
 *   - Server-side errors are surfaced inline (RR-0004 WIP friendly-error preserved)
 *   - Dialog stays open on error; closes on success
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal/Modal';
import { Alert } from '@/components/ui/Alert';
import { Textarea } from '@/components/ui/Textarea';
import { t } from '@/lib/i18n';

export interface ReasonDialogProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  /**
   * Returns a Promise so the dialog owns its own close-on-success lifecycle (RR-0004 fix).
   * Throw to show error inline (dialog stays open).
   */
  onSubmit: (reason: string) => Promise<void>;
  isPending: boolean;
}

export function ReasonDialog({ isOpen, title, onClose, onSubmit, isPending }: ReasonDialogProps) {
  const [reason, setReason] = useState('');
  const [validationError, setValidationError] = useState('');
  // RR-0004: surface server-side submission errors inline in the dialog (persistent Alert).
  const [submissionError, setSubmissionError] = useState('');

  // Reset state every time the dialog opens
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setValidationError('');
      setSubmissionError('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setValidationError(t('Reason is required'));
      return;
    }
    setSubmissionError('');
    try {
      await onSubmit(reason.trim());
      // Success — close the dialog. Parent already ran toast + refetch.
      onClose();
    } catch (err: any) {
      // Keep dialog open and show the friendly error inline (RR-0004).
      const msg = (err?.message as string) || t('Could not submit your request, please try again.');
      setSubmissionError(msg);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <div className="space-y-4 pt-2">
        {validationError && (
          <Alert variant="error" title={t('Validation error')}>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              <span>{validationError}</span>
            </div>
          </Alert>
        )}
        {submissionError && !validationError && (
          <Alert variant="error" title={t('Could not submit your request')}>
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden />
              <span data-testid="reason-dialog-submission-error">{submissionError}</span>
            </div>
          </Alert>
        )}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('Reason')} <span className="text-error-500" aria-hidden>*</span>
          </label>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (validationError) setValidationError('');
              if (submissionError) setSubmissionError('');
            }}
            placeholder={t('Describe what changes are needed…')}
            required
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
            {t('Cancel')}
          </Button>
          <Button type="button" onClick={handleSubmit} variant="error" isLoading={isPending}>
            {t('Submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
